import React, { createRef } from 'react';
import { compose, mapProps } from 'recompose';
import { withRouter } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { getObjValue, isDifferent } from 'utils/functions';
import { graphql, withApollo } from 'react-apollo';
import { getQueryFilterVariables } from 'selectors/assessmentFilters/selector';
import { getUpdatedCategoryListAfterFilter, isAssessmentReadOnly as isAssessmentReadOnlySelector } from 'selectors/assessment/selector';
import { updateAssigneesForCategory, listFieldsForCategory } from 'graphql/operations';

import { CategoryItemHOC } from 'components/common/SideBar/CategoryItem';
import { PanelHeader } from 'components/common/Panel/Header';
import EventEmitter from 'utils/events';
import { EventEmitterTypes } from 'utils/constants';

import './CategorySidebar.scss';

export class CategorySideBarContent extends React.Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    // TODO: Fix these non-specific prop types
    /* eslint-disable react/forbid-prop-types */
    fieldsFilter: PropTypes.object,
    client: PropTypes.object.isRequired,
    categories: PropTypes.array.isRequired,
    assessment: PropTypes.object.isRequired,
    /* eslint-enable react/forbid-prop-types */
    updateAssigneesForCategory: PropTypes.func.isRequired,
    isAssessmentReadOnly: PropTypes.bool
  };

  static defaultProps = {
    fieldsFilter: null,
    isAssessmentReadOnly: false
  };

  constructor(props) {
    super(props);
    this.state = this.initState();
  }

  getNextQueryVariables = (assessmentId, categoryId, fieldsFilter) => {
    return {
      assessmentId,
      categoryId,
      first: 1000,
      after: null,
      fieldsFilter
    };
  };

  componentDidUpdate(prevProps) {
    const { assessment, categories, fieldsFilter, assessmentParticipants } = this.props;
    const { queryVariables, filteredCategories } = this.state;

    const categoriesChanged = isDifferent(prevProps.categories, categories);
    if (categoriesChanged) {
      const updatedFilteredCategories = this.updateFilteredCategories(filteredCategories, categories);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ filteredCategories: updatedFilteredCategories });
    }

    // Re-init state to trigger data fetch with new filters
    const fieldsFilterChanged = isDifferent(prevProps.fieldsFilter, fieldsFilter);
    if (fieldsFilterChanged) {
      const updatedFilteredCategories = fieldsFilter ? getUpdatedCategoryListAfterFilter({ assessment, fieldsFilter }) : categories;
      updatedFilteredCategories.length > 0 && updatedFilteredCategories.sort((a, b) => (a.order > b.order ? 1 : -1));
      // TODO: Figure this next issue out as it's a big code smell...
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        ...this.initState(),
        filteredCategories: updatedFilteredCategories
      });
      return;
    }

    // Remove assignees from local data store
    const newParticipants = assessmentParticipants;
    const prevParticipants = prevProps.assessmentParticipants;

    if (newParticipants.length < prevParticipants.length) {
      const users = _.differenceBy(prevParticipants, newParticipants, 'email');
      const email = users.map(user => user.email);
      this.addRemoveQuestionParticipants(queryVariables, [], email);
    }
  }

  updateFilteredCategories = (filteredCategories, categories) =>
    filteredCategories && filteredCategories.map(f => categories && (categories.find(c => c.id === f.id) || f));

  initState = () => {
    const { categories } = this.props;
    return {
      selection: '',
      categoryOrder: null,
      queryVariables: [],
      filteredCategories: categories
    };
  };

  setSelection = (categoryName, categoryOrder) => {
    const { selectedCategory } = this.props;
    this.setState({ selection: categoryName });
    selectedCategory(categoryOrder);
    const { currentCategory } = this.props;
    if (categoryOrder < currentCategory?.order || currentCategory === null) {
      EventEmitter.dispatch(EventEmitterTypes.SCROLL_INTO_VIEW, { selectedCategoryOrder: categoryOrder });
    }
    EventEmitter.dispatch(EventEmitterTypes.TOGGLE_MOBILE_SIDEBAR, { right: 'right', open: false });
  };

  getQueryVariables = () => {
    const queryVariableArray = [];
    const { assessment, categories, fieldsFilter } = this.props;
    categories.forEach(category => {
      queryVariableArray.push(this.getNextQueryVariables(assessment.id, category.id, fieldsFilter));
    });
    return queryVariableArray;
  };

  onBulkAssignChange = (categoryId, addedParticipants, removedParticipants) => {
    const queryVariables = this.getQueryVariables();
    this.setState({ queryVariables });
    const filteredQueryVariables = queryVariables.filter(qv => qv.categoryId === categoryId);
    this.addRemoveQuestionParticipants(filteredQueryVariables, addedParticipants, removedParticipants);
  };

  getUpdatedAssignees = (assessmentParticipants, current, added, removed) => {
    let updatedAssignees = [];

    // Remove assignees
    if (current.length > 0 && removed.length > 0) {
      const keptUsers = current.filter(user => !removed.find(r => r === user.email));
      updatedAssignees = keptUsers;
    } else {
      updatedAssignees = [...current];
    }

    // Add assignees
    if (added.length > 0) {
      const addedUsers = assessmentParticipants.filter(p => added.find(a => a === p.email));
      updatedAssignees = _.unionBy(updatedAssignees, addedUsers, 'email');
    }

    return updatedAssignees;
  };

  updateLocalStore = args => {
    const { client } = this.props;
    return client.writeQuery(args);
  };

  // Update local store when there is a change in participants that impacts any questions
  addRemoveQuestionParticipants(queryVariables, addedParticipants, removedParticipants) {
    const { assessmentParticipants, client } = this.props;

    queryVariables.forEach(variables => {
      try {
        const result = client.readQuery({ query: listFieldsForCategory, variables });

        const updatedEdges = result.listFieldsForCategory.edges.map(edge => {
          const current = edge.node.assignees;
          return {
            ...edge,
            node: {
              ...edge.node,
              assignees: this.getUpdatedAssignees(assessmentParticipants, current, addedParticipants, removedParticipants)
            }
          };
        });
        const updatedFieldsForCategory = {
          ...result.listFieldsForCategory,
          edges: updatedEdges
        };
        const data = { listFieldsForCategory: updatedFieldsForCategory };
        // Using defer to give Apollo time to update store
        _.defer(this.updateLocalStore, { query: listFieldsForCategory, variables, data }, 300);
      } catch (error) {
        // Query is not loaded yet. Ignore. Can happen due to assigning/ removing participants from section panel but section is not loaded yet.
      }
    });
  }

  render() {
    const { assessment, t, actions, updateAssigneesForCategory: propsUpdateAssigneesForCategory, isAssessmentReadOnly, visibleCategory } = this.props;
    const { filteredCategories, selection } = this.state;
    const categoryItem = filteredCategories?.map(section => {
      const categoryNameRef = createRef();
      return (
        <CategoryItemHOC
          key={section.id}
          assessment={assessment}
          actions={actions}
          category={section}
          updateAssigneesForCategory={propsUpdateAssigneesForCategory}
          onBulkAssignChange={this.onBulkAssignChange}
          selection={selection}
          setSelection={this.setSelection}
          t={t}
          isAssessmentReadOnly={isAssessmentReadOnly}
          visibleCategory={visibleCategory}
          categoryNameRef={categoryNameRef}
        />
      );
    });
    return (
      <div className="section-navigation-panel">
        <div className="guided-tour-category-list-wrapper">
          <div className="guided-tour-category-list" />
        </div>
        <PanelHeader title={t('assessment.sectionTitle')} />
        <div className="section-list-content">{categoryItem}</div>
      </div>
    );
  }
}

const CategorySidebar = compose(
  withRouter,
  graphql(updateAssigneesForCategory, { name: 'updateAssigneesForCategory' }),
  withTranslation(),
  withApollo,
  mapProps(props => ({
    client: props.client,
    t: props.t,
    fieldsFilter: getQueryFilterVariables(props),
    updateAssigneesForCategory: props.updateAssigneesForCategory,
    assessmentParticipants: getObjValue(props, 'assessment.participants', []),
    assessment: props.assessment,
    categories: getObjValue(props, 'assessment.categories', []),
    actions: props.actions,
    isAssessmentReadOnly: isAssessmentReadOnlySelector(props),
    selectedCategory: props.selectedCategory,
    visibleCategory: props.visibleCategory,
    currentCategory: props.currentCategory
  }))
)(CategorySideBarContent);

export { CategorySidebar };
